import { useParams, useSearchParams } from 'react-router-dom';

import { PDFReportType, Project, Result } from '@gameonly/core';
import useUser from '../../authentication/context/useUser';
import { PdfCompleteReport } from '../../pdf/PdfCompleteReport';
import { getProjectYear, PageType, PaginatedResult } from '../../pdf/utils';
import useReport from '../context/useReport';
import { annualReportModel } from '../Report';

const AnnualReportPdf = () => {
  const { reportId } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useUser();
  const { report } = useReport(reportId);

  const reportType = searchParams.get('reportType') as PDFReportType;

  if (!report || !user) {
    return null;
  }

  const reportDate = new Date();
  const years = getReportYear(report.projects);
  const year = years.length === 1 ? years[0] : undefined;

  let paginatedResults = getPaginatedResults(report.results, reportType);

  if (reportType === PDFReportType.Simple) {
    paginatedResults = [
      {
        pageType: PageType.COVER_SIMPLE,
      },
      ...getPaginatedResultForSimple(paginatedResults, ['co2', 'beges', 'ghg']),
      {
        pageType: PageType.BACK_COVER,
      },
    ];
  } else {
    paginatedResults = [
      {
        pageType: PageType.COVER_COMPLETE,
      },
      ...paginatedResults,
      {
        pageType: PageType.BACK_COVER,
      },
    ];
  }

  return (
    <PdfCompleteReport
      key={report._id.toString()}
      reportDate={reportDate}
      paginatedResults={paginatedResults}
      year={year}
      model={annualReportModel}
    />
  );
};

const getPaginatedResults = (results: Result[], reportType: PDFReportType) => {
  let paginatedResults: PaginatedResult[] = [];
  for (let i = 0; i < results.length; ) {
    const result = results[i];

    if (result.type === 'title' && !result.text?.includes('BEGES')) {
      const titleGroup = results.slice(i, i + 2);
      paginatedResults.push({
        pageType: PageType.MAIN_INDICATOR,
        results: titleGroup,
      });
      i += 2;
      paginatedResults.push({
        pageType: PageType.RESULT_FULL_PAGE,
        results: [results[i]],
      });
      i += 1;
      continue;
    }

    if (result.type === 'table') {
      paginatedResults.push({
        pageType: PageType.RESULT_FULL_PAGE,
        results: [results[i]],
      });
      i += 1;
      continue;
    }
    i += 1;
  }
  return paginatedResults;
};

const getPaginatedResultForSimple = (
  data: PaginatedResult[],
  keywords: string[],
): PaginatedResult[] => {
  const keywordRegex = new RegExp(keywords.join('|'), 'i');

  return data.filter((paginated) =>
    paginated.results?.some(
      (result) => result.code && keywordRegex.test(result.code),
    ),
  );
};

const getReportYear = (projects: Project[]) => {
  const years = projects
    .map((project) => getProjectYear(project))
    .filter((year) => year !== undefined);
  return Array.from(new Set(years));
};

export default AnnualReportPdf;
